import { useMemo, useState } from "react";
import { parsePriceArs } from "../../shared/marketImport";
import { dollarPrice, newPrice, PRICE_OPS, PRICE_ROUNDINGS, type PriceOp, type PriceRounding } from "../../shared/marketPrices";
import { useAsync } from "../lib/useAsync";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { marketApi, type MyListing } from "./api";
import { cardSpecs, useArs } from "./components";
import { matchesText } from "./ListingFilters";

type StatusFilter = "all" | "active" | "paused";
/** The usual ops, or "Ajustar al dólar" (each price × today's dólar / the dólar it was set at). */
type Op = PriceOp | "dollar";

/**
 * Change many prices at once: pick listings (filtered by status or by name/set), say how (a percent,
 * pesos up or down, or one price) and the rounding, check the old → new preview and save.
 */
export function BulkPrices({
  listings,
  onClose,
  onSaved,
  dollar = false,
  maxPriceArs = null,
}: {
  listings: MyListing[];
  onClose: () => void;
  onSaved: () => void;
  /** Opened from "Ajustar al dólar": that op, every listing ticked. */
  dollar?: boolean;
  /** The plan's highest price (null: none): a live listing isn't raised past it. */
  maxPriceArs?: number | null;
}) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const [status, setStatus] = useState<StatusFilter>("all");
  const [text, setText] = useState("");
  const [selected, setSelected] = useState<Set<number>>(() => new Set(dollar ? listings.map((l) => l.id) : []));
  const [op, setOp] = useState<Op>(dollar ? "dollar" : "pct_down");
  const info = useAsync((signal) => (op === "dollar" ? marketApi.info(signal) : Promise.resolve(null)), [op === "dollar"]);
  const rateNow = info.data?.usdArsRate ?? null;
  const [value, setValue] = useState("");
  // Prices that follow the dólar come out odd: rounded to $100 unless the seller says otherwise.
  const [round, setRound] = useState<PriceRounding>(dollar ? 100 : 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [result, setResult] = useState<{ updated: number; gone: number; capped: number } | null>(null);

  const shown = useMemo(() => {
    return listings.filter((l) => (status === "all" || l.status === status) && matchesText(l, text));
  }, [listings, status, text]);

  // Each row's new price, or why it stays as it is.
  // Percent may have decimals ("2,5"); pesos are whole, written the Argentine way ("1.500").
  const amount = op.startsWith("pct") ? Number(value.trim().replace(",", ".")) : (parsePriceArs(value) ?? Number.NaN);
  const plan = (l: MyListing): { price: number | null; why: "same" | "invalid" | "norate" | "cap" | "usd" | null } => {
    if (!selected.has(l.id)) return { price: null, why: null };
    // Priced in dollars: its pesos follow the dólar on their own (changed from the listing itself).
    if (l.priceUsdCents != null) return { price: null, why: "usd" };
    if (op !== "dollar" && !value.trim()) return { price: null, why: null };
    if (op === "dollar" && !rateNow) return { price: null, why: null };
    if (op === "dollar" && !l.priceRate) return { price: null, why: "norate" };
    const p = op === "dollar" ? dollarPrice(l.priceArs, l.priceRate, rateNow, round) : newPrice(l.priceArs, op, amount, round);
    if (p === null) return { price: null, why: "invalid" };
    if (p === l.priceArs) return { price: null, why: "same" };
    // As the server does: a live listing isn't raised above the plan's highest price.
    if (l.status === "active" && maxPriceArs !== null && p > maxPriceArs && p > l.priceArs) return { price: p, why: "cap" };
    return { price: p, why: null };
  };
  // Only what's on screen: a selection hidden by the filters is never saved.
  const picked = shown.filter((l) => selected.has(l.id));
  const changes = picked
    .map((l) => ({ l, ...plan(l) }))
    .filter((r) => r.price !== null && r.why === null)
    .map((r) => ({ id: r.l.id, priceArs: r.price! }));

  const allShown = shown.length > 0 && shown.every((l) => selected.has(l.id));
  const toggleAll = () => {
    const next = new Set(selected);
    shown.forEach((l) => (allShown ? next.delete(l.id) : next.add(l.id)));
    setSelected(next);
  };
  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const save = async () => {
    setBusy(true);
    setError(false);
    try {
      const r = await marketApi.updatePrices(changes);
      const n = (code: string) => r.skipped.filter((s) => s.code === code).length;
      setResult({ updated: r.updated, gone: n("not_found"), capped: n("price_limit") });
      setSelected(new Set());
      onSaved();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="new-listing bulk-prices stack" aria-labelledby="bp-title">
      <h2 id="bp-title">{t("bulk.title")}</h2>
      {result && (
        <p className="notice" role="status">
          {[t("bulk.updated", { count: result.updated }), result.gone > 0 && t("bulk.skippedGone", { count: result.gone }), result.capped > 0 && t("bulk.skippedPrice", { count: result.capped })].filter(Boolean).join(" · ")}
        </p>
      )}
      <div className="field-row">
        <div className="field">
          <label htmlFor="bp-text">{t("bulk.search")}</label>
          <input id="bp-text" type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("bulk.searchHint")} />
        </div>
        <div className="field">
          <label htmlFor="bp-status">{t("bulk.status")}</label>
          <select id="bp-status" value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
            <option value="all">{t("bulk.statusAll")}</option>
            <option value="active">{t("bulk.statusActive")}</option>
            <option value="paused">{t("bulk.statusPaused")}</option>
          </select>
        </div>
      </div>
      <div className="field-row bulk-op">
        <div className="field">
          <label htmlFor="bp-op">{t("bulk.op")}</label>
          <select id="bp-op" value={op} onChange={(e) => setOp(e.target.value as Op)}>
            {PRICE_OPS.map((o) => (
              <option key={o} value={o}>
                {t(`bulk.op.${o}` as MessageKey)}
              </option>
            ))}
            <option value="dollar">{t("bulk.op.dollar")}</option>
          </select>
        </div>
        {op !== "dollar" && (
          <div className="field">
            <label htmlFor="bp-value">{op.startsWith("pct") ? t("bulk.valuePct") : t("bulk.valueArs")}</label>
            <input id="bp-value" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
          </div>
        )}
        <div className="field">
          <label htmlFor="bp-round">{t("bulk.round")}</label>
          <select id="bp-round" value={round} onChange={(e) => setRound(Number(e.target.value) as PriceRounding)}>
            {PRICE_ROUNDINGS.map((r) => (
              <option key={r} value={r}>
                {r ? t("bulk.roundTo", { amount: ars(r) }) : t("bulk.roundNone")}
              </option>
            ))}
          </select>
        </div>
      </div>

      {op === "dollar" && (
        <p className="notice small">
          {rateNow
            ? info.data?.usdArsRateAt
              ? t("bulk.dollarToday", { rate: ars(rateNow), date: new Date(info.data.usdArsRateAt).toLocaleDateString(locale === "es" ? "es-AR" : "en-US", { day: "numeric", month: "numeric" }) })
              : t("bulk.dollarTodayNoDate", { rate: ars(rateNow) })
            : info.data
              ? t("bulk.dollarNoRate")
              : t("bulk.dollarLoading")}{" "}
          {t("bulk.dollarHow")}
        </p>
      )}
      {op === "dollar" && rateNow && picked.length > 0 && changes.length === 0 && picked.every((l) => ["same", "norate", "usd"].includes(plan(l).why ?? "")) && <p className="muted">{t("bulk.dollarNothing")}</p>}
      {shown.length === 0 ? (
        <p className="muted">{t("bulk.none")}</p>
      ) : (
        <div className="imp-table-wrap">
          <table className="bulk-table">
            <thead>
              <tr>
                <th scope="col">
                  <label className="bulk-all">
                    <input type="checkbox" checked={allShown} onChange={toggleAll} aria-label={t("bulk.selectAll")} />
                    <span aria-hidden="true">{t("bulk.statusAll")}</span>
                  </label>
                </th>
                <th scope="col">{t("imp.product")}</th>
                <th scope="col" className="num">
                  {t("bulk.price")}
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((l) => {
                const r = plan(l);
                return (
                  <tr key={l.id} className={selected.has(l.id) ? "" : "is-off"}>
                    <td>
                      <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggle(l.id)} aria-label={t("bulk.select", { name: l.name })} />
                    </td>
                    <td>
                      <strong>{l.name}</strong>
                      <span className="muted small">
                        {cardSpecs(l, locale)}
                        {l.status === "paused" ? ` · ${t("bulk.paused")}` : ""}
                        {op === "dollar" && l.priceRate ? ` · ${t("bulk.dollarThen", { rate: ars(l.priceRate) })}` : ""}
                      </span>
                      {r.why === "norate" && <span className="muted small">{t("bulk.dollarUnknown")}</span>}
                      {r.why === "usd" && <span className="muted small">{t("bulk.usdListing")}</span>}
                      {r.why === "cap" && <span className="field-error">{t("bulk.overCap", { max: ars(maxPriceArs ?? 0) })}</span>}
                      {r.why === "invalid" && <span className="field-error">{t("bulk.invalid")}</span>}
                    </td>
                    <td className="num">
                      {r.price !== null && r.why === null ? (
                        <span>
                          <s className="muted">{ars(l.priceArs)}</s> → <strong>{ars(r.price)}</strong>
                        </span>
                      ) : (
                        ars(l.priceArs)
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted small" role="status">
        {t("bulk.selected", { count: picked.length })}
      </p>
      {error && <p className="form-error">{t("error.generic")}</p>}
      <div className="row-actions">
        <button type="button" className="btn btn-primary" disabled={busy || changes.length === 0} onClick={save}>
          {t("bulk.save", { count: changes.length })}
        </button>
        <button type="button" className="btn btn-link" onClick={onClose}>
          {t("bulk.close")}
        </button>
      </div>
    </section>
  );
}
